const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
export const sleepSeconds = (seconds: number) => sleep(seconds * 1000)

/**
 * 非同期 task をリトライする
 *
 * @param task
 * @param options.maxRetryCount デフォルト: 3
 * @param options.backOff デフォルト: 1.5 ** triedCount * 1000
 * @param options.errorsMapper デフォルト: 発生したエラーの配列を文字列にしたメッセージを格納した Error に変換する
 * @returns
 */
export const retry = async <T>(
  task: () => Promise<T>,
  options: {
    maxRetryCount?: number
    backOff?: (triedCount: number) => number
    errorsMapper?: (errors: unknown[]) => Error
  } = {},
): Promise<T | Error> => {
  const maxRetryCount = options.maxRetryCount ?? 3
  const backOff =
    options.backOff ?? ((triedCount: number) => 1.5 ** triedCount * 1000)
  const errorsMapper =
    options.errorsMapper ?? ((errors: unknown[]) => new Error(String(errors)))

  let triedCount = 0
  const errors: unknown[] = []
  while (triedCount < maxRetryCount) {
    try {
      if (triedCount) {
        await sleep(backOff(triedCount))
      }
      return await task()
    } catch (e) {
      errors.push(e)
    }
    triedCount++
  }
  return errorsMapper(errors)
}

/**
 * items を同時実行数 limit のローリングプールで処理する。
 * 1件終わるごとに次を投入するため、固定チャンクで待ち合わせる方式より遅延が少ない。
 * 結果は入力順で返す。fn が reject した場合は全体が reject するため、
 * 1件の失敗で全体を止めたくない場合は fn 側で try/catch すること。
 */
export const mapWithConcurrency = async <T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
  const results = new Array<R>(items.length)
  let nextIndex = 0
  const worker = async (): Promise<void> => {
    while (nextIndex < items.length) {
      const current = nextIndex
      nextIndex += 1
      results[current] = await fn(items[current], current)
    }
  }
  const workerCount = Math.max(1, Math.min(limit, items.length))
  await Promise.all(Array.from({ length: workerCount }, () => worker()))
  return results
}
